import { deriveReelSections } from '@src/services/reel/deriveReelSections';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../../models/TranscriptTrack';
import { EditorChangeType } from '../../types';
import { AbstractCommand } from '../AbstractCommand';
import { applyReelSectionOrder } from './applyReelSectionOrder';

import type { Scene } from '../../models/layout/Scene';
import type { VideoEditor } from '../../VideoEditor';

/**
 * PLAT-11056 — moves a whole Chapter to a new position in playback order.
 *
 * A Chapter is a contiguous run of Sections (DE12), so moving one is the same
 * timeline operation as moving a Section, applied to the run (DE6) — one
 * permutation, one re-layout, one carry of every owned element, one
 * re-projection, and therefore **one undo entry** for the gesture the creator
 * made (DS1). Issuing a Section move per member would instead leave N entries
 * and pass through intermediate states in which the Chapter is half moved.
 *
 * Chapters themselves live in `clipJson.reel`, which the editor domain does not
 * read (DE13) — so the caller resolves the Chapter to its Sections and this
 * stays a timeline operation over a list of ids. Nothing is written back: each
 * Chapter is anchored to its own first Section, which travels with it, so the
 * boundaries are still correct once the run has moved (DS6).
 *
 * @param sectionIds The Chapter's Sections, in playback order
 * @param targetIndex Where the run is re-inserted, in the order *after* it has
 *   been lifted out — the same convention {@link MoveReelSectionCommand} uses
 */
export class MoveReelChapterCommand extends AbstractCommand {
  private sectionIds: readonly string[];
  private targetIndex: number;
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private originalTranscriptTrackId: string | undefined;
  /**
   * The scenes as they stood before the re-layout re-anchored them.
   * {@link TimelineSnapshot} carries tracks and elements but not scenes, so the
   * same reason {@link RemoveReelSectionCommand} keeps its own copy applies
   * here: without it an undo puts the old element times back underneath the
   * new anchors.
   */
  private previousScenes: Scene[] | undefined;

  constructor(
    editor: VideoEditor,
    sectionIds: readonly string[],
    targetIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.sectionIds = sectionIds;
    this.targetIndex = targetIndex;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'MoveReelChapterCommand';
  }

  getDescription(): string {
    return `Move reel chapter of ${this.sectionIds.length} section(s) to index ${this.targetIndex}`;
  }

  execute(): boolean {
    if (this.executed) return false;

    const timeline = this.editor.timeline;
    const sections = deriveReelSections(timeline);
    const positions = this.sectionIds.map(id =>
      sections.findIndex(section => section.id === id),
    );

    if (positions.length === 0 || positions.includes(-1)) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'section_not_found',
        sectionIds: this.sectionIds,
      });
      return false;
    }

    const fromIndex = positions[0];
    // A Chapter *is* a contiguous span (DE12), so a run that is not contiguous
    // is a derivation bug — better refused than re-laid out into whatever
    // order the ids happened to arrive in.
    if (positions.some((position, offset) => position !== fromIndex + offset)) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'chapter_not_contiguous',
        positions,
      });
      return false;
    }

    const runLength = positions.length;
    const clampedTarget = Math.max(
      0,
      Math.min(this.targetIndex, sections.length - runLength),
    );
    if (clampedTarget === fromIndex) {
      // A drag that lands where it started is not an edit — no undo entry.
      return false;
    }

    const track = timeline.tracks.find(
      (t): t is TranscriptTrack => t instanceof TranscriptTrack,
    );
    if (!track) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    super.execute(); // captures the timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        sectionIds: this.sectionIds,
        fromIndex,
        targetIndex: clampedTarget,
      });

      this.originalTranscriptTrackId = this.editor.getTranscriptTrack()?.id;
      this.previousScenes = [...timeline.scenes];

      const order = [...sections];
      const run = order.splice(fromIndex, runLength);
      order.splice(clampedTarget, 0, ...run);

      const { restored } = applyReelSectionOrder(
        this.editor,
        track,
        sections,
        order,
        this.exemptElementIds,
      );
      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
      }

      //settles the captions and the pinned cards
      // against their new positions.
      this.recalculateTimeline();

      this.editor.markDirty('move_reel_chapter');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'reel_chapter_moved',
        sectionIds: this.sectionIds,
        fromIndex,
        toIndex: clampedTarget,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        sectionIds: this.sectionIds,
        fromIndex,
        toIndex: clampedTarget,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    const result = super.undo();

    // The transcript restore re-pointed transcriptTrackId at the rebuilt
    // track; the snapshot restores the tracks but not that id.
    if (result && this.originalTranscriptTrackId) {
      this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
    }
    if (result && this.previousScenes) {
      this.editor.timeline.setScenes(this.previousScenes);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
